// Calcula la franja objetivo que se muestra en la gráfica de Balance
// (referencia del objetivo ACTUAL, no varía día a día). Si hay TDEE
// calculado, la franja se centra en el déficit/superávit que se
// pretende (objetivo - TDEE) con ±100 kcal. Si no hay TDEE, se usa
// el cálculo aproximado de antes (relativo al objetivo de ingesta).
export function getBalanceTargets(kcalObj, tdee) {
  const hasTdee = tdee != null && tdee > 0
  const centro = hasTdee ? (kcalObj - tdee) : null

  return {
    minTarget: hasTdee ? centro - 100 : -kcalObj * 0.35,
    maxTarget: hasTdee ? centro + 100 : -kcalObj * 0.1,
    isApprox: !hasTdee,
    mapKcal: hasTdee ? (kcal) => kcal - tdee : (kcal) => kcal - kcalObj,
  }
}

// Busca, dentro del historial de objetivo/TDEE, cuál estaba vigente
// en una fecha concreta (la fila más reciente cuya "vigente_desde"
// sea igual o anterior a esa fecha).
export function resolveTargetsForDate(sortedHistory, fecha, fallbackKcalObj, fallbackTdee) {
  let applicable = null
  for (const h of sortedHistory) {
    if (h.vigente_desde <= fecha) applicable = h
    else break
  }
  return {
    kcalObj: applicable?.kcal_objetivo ?? fallbackKcalObj,
    tdee: applicable?.tdee ?? fallbackTdee,
  }
}

// Añade a cada día de un historial de comidas (kcal/prot/gras/carb
// por fecha) su balance real (v), calculado con el objetivo/TDEE que
// estaba vigente ESE día concreto — no el de hoy. Así, un cambio de
// objetivo no recolorea retroactivamente los días ya pasados.
export function withHistoricalBalance(dailyHistory, targetsHistory, fallbackKcalObj, fallbackTdee) {
  const sorted = [...(targetsHistory ?? [])].sort((a, b) => (a.vigente_desde < b.vigente_desde ? -1 : 1))
  return dailyHistory.map(d => {
    // Un día sin ninguna comida registrada no es un balance de -TDEE:
    // es un día sin datos, y no debe dibujarse ni entrar en las medias.
    if (!(d.kcal > 0)) return { ...d, v: null, sinDatos: true }
    const { kcalObj, tdee } = resolveTargetsForDate(sorted, d.fecha, fallbackKcalObj, fallbackTdee)
    const hasTdee = tdee != null && tdee > 0
    const v = hasTdee ? d.kcal - tdee : d.kcal - kcalObj
    return { ...d, v, sinDatos: false }
  })
}

function addDaysIso(iso, n) {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}

// El historial solo trae los días en los que se abrió la app. Para que un
// día completamente en blanco aparezca como "sin datos" (y no desaparezca
// de la gráfica), se rellenan los días intermedios hasta ayer. No se
// rellena antes del primer registro: esos días el paciente aún no existía.
export function fillMissingDays(history, todayFecha) {
  if (!history.length) return history
  const byDate = new Map(history.map(d => [d.fecha, d]))
  const end = addDaysIso(todayFecha, -1)
  const out = []
  for (let f = history[0].fecha; f <= end; f = addDaysIso(f, 1)) {
    const [y, m, d] = f.split('-').map(Number)
    out.push(byDate.get(f) ?? {
      fecha: f,
      label: new Date(y, m - 1, d).toLocaleDateString('es', { day: '2-digit', month: 'short' }),
      kcal: 0, prot: 0, gras: 0, carb: 0,
    })
  }
  return out
}
